import {Country} from '@hconnect/apiclient'
import {flagPathByCountryCode} from '@hconnect/uikit'
import {LabelRegular, TextMedium} from '@hm/fundamental'
import React from 'react'
import {Controller, useFormContext} from 'react-hook-form'

import {InputSkeleton} from '../../../common/skeletons/InputSkeleton'
import {useTranslation} from '../../../hooks/useTranslation'
import {SelectGrouped} from '../../../pages/ManageUser/components/SelectGrouped'
import {CompanyFormData} from '../CompanyForm.types'

type CompanyCountryInputProps = {
  readonlyMode: boolean
  countriesIds: string[]
  isLoading: boolean
  selectedCountry?: Country
}

export const CompanyCountryInput = ({
  readonlyMode,
  countriesIds,
  isLoading,
  selectedCountry
}: CompanyCountryInputProps) => {
  const {t} = useTranslation()
  const formMethods = useFormContext<CompanyFormData>()
  const errors = formMethods.formState.errors

  if (isLoading) {
    return <InputSkeleton />
  }

  const countriesData = window.intlTelInputGlobals.getCountryData()

  const countryDataFilter = (countryId: string) =>
    countryId === 'MOCK'
      ? {name: 'MOCK', iso2: 'MOCK', dialCode: 'MOCK'}
      : countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)

  const formattedCountryData = countriesIds
    .filter((countryId) => countryDataFilter(countryId))
    .map((countryId) => {
      return {
        label: countryDataFilter(countryId)?.name ?? '',
        value: countryDataFilter(countryId)?.iso2.toUpperCase() ?? ''
      }
    })
    .sort((current, next) => {
      if (current.label < next.label) {
        return -1
      }
      if (current.label > next.label) {
        return 1
      }
      return 0
    })

  const errorText = errors.country?.message ? String(errors.country.message) : ''

  const shouldDisplayFlag = selectedCountry?.countryCode && selectedCountry.countryCode !== 'MOCK'

  return (
    <>
      {readonlyMode && selectedCountry ? (
        <div className="flex flex-col mb-4 px-3" data-test-id="company-form-country">
          <LabelRegular className="text-foreground_faded mb-1">
            {t('companyForm.country')}
          </LabelRegular>
          <div className="flex gap-2 items-center">
            {shouldDisplayFlag && (
              <img
                src={flagPathByCountryCode(selectedCountry.countryCode)}
                alt={`${selectedCountry.countryCode}-flag`}
                className="max-h-[16px]"
              />
            )}
            <TextMedium className="text-foreground">
              {formattedCountryData.find(
                (countryData) => countryData.value === selectedCountry?.countryCode.toUpperCase()
              )?.label || selectedCountry?.countryName}
            </TextMedium>
          </div>
        </div>
      ) : (
        countriesIds.length > 1 && (
          <div className="w-full [&>div]:max-w-none">
            <Controller
              name="country"
              control={formMethods.control}
              render={({field}) => (
                <SelectGrouped
                  {...field}
                  inputLabel={t('companyForm.country')}
                  value={field.value || '-'}
                  data-test-id={`CompanyForm-${field.name}`}
                  onChange={(value) => {
                    formMethods.setValue('country', value, {
                      shouldValidate: true
                    })
                    field.onChange(value)
                  }}
                  error={errorText}
                  options={formattedCountryData}
                  showSearch={true}
                  searchPlaceholder={`${t('companyForm.select')} ${t('companyForm.country')}`}
                  isReadOnly={readonlyMode}
                />
              )}
            />
          </div>
        )
      )}
    </>
  )
}
